import { useSelector } from 'react-redux';
import { State } from 'redux/initialState';
import useRolesValidation from 'hooks/useRolesValidation';
import { getMenusVisibility } from '../utils/getMenusVisibility';

type UseExtendedMenuProps = {
  societyId: string;
  userId?: string | null;
};

export const useExtendedMenu = ({
  societyId,
  userId,
}: UseExtendedMenuProps) => {
  const { hasAccessCaptable, isSocietyHolding, configuration } = useSelector(
    (state: State) => state?.society?.actualSociety
  ) || {
    hasAccessCaptable: false,
    isSocietyHolding: false,
    configuration: {},
  };

  const actualSociety = useSelector(
    (state: State) => state?.society?.actualSociety
  );
  const societyPermissions = useSelector(
    (state: State) => state?.society?.permissions
  );
  const hasCustomLanding = useSelector(
    (state: State) =>
      state?.society?.actualSociety?.configuration?.hasCustomLanding
  );
  const { role } = useSelector((state: State) => state?.society) || {};
  const { user } = useSelector((state: State) => state);
  const isDemo = useSelector((state: State) => state.society?.role?.isDemo);

  const rolesValidation = useRolesValidation(role);

  const {
    isAdmin,
    isPartner,
    isBeneficiary,
    isInvestor,
    isRepresentant,
    isGuest,
    isHolder,
    isDirector,
  } = useSelector(
    (state: State) =>
      state?.society?.role || {
        isAdmin: false,
        isPartner: false,
        isBeneficiary: false,
        isInvestor: false,
        isRepresentant: false,
        isGuest: false,
        isHolder: false,
        isDirector: false,
      }
  );

  const {
    hasCustomLandingMenu,
    hasDashboardMenu,
    hasPartnersMenu,
    hasPortfolioMenu,
    hasAdministrativeMenu,
    hasBoardMenu,
    hasTransactionsMenu,
    hasEquityMarketMenu,
    hasPlansMenu,
    hasDraftsMenu,
    hasSignaturesMenu,
    hasDocumentsMenu,
    hasSocietyDataMenu,
    hasCommunicationsMenu,
  } = getMenusVisibility({
    rolesValidation,
    societyPermissions,
    hasCustomLanding: hasCustomLanding ?? false,
    isSocietyHolding,
    actualSociety,
    user,
  });

  const getBoardLinkByRole = () => {
    if (isAdmin) {
      return `/juntas/${societyId}`;
    }

    if (isPartner) {
      return `/juntas/${societyId}/${userId}`;
    }

    if (isRepresentant) {
      return `/juntas/${societyId}/${userId}`;
    }

    return '';
  };

  return {
    data: {
      // Redux state
      hasAccessCaptable,
      isSocietyHolding,
      configuration,
      actualSociety,
      societyPermissions,
      hasCustomLanding,
      role,
      user,
      isDemo,

      // Role validation
      rolesValidation,

      // Role properties
      isAdmin,
      isPartner,
      isBeneficiary,
      isInvestor,
      isRepresentant,
      isGuest,
      isHolder,
      isDirector,

      // Menu visibility
      hasCustomLandingMenu,
      hasDashboardMenu,
      hasPartnersMenu,
      hasDraftsMenu,
      hasCommunicationsMenu,
      hasBoardMenu,
      hasSignaturesMenu,
      hasDocumentsMenu,
      hasSocietyDataMenu,
      hasPlansMenu,
      hasTransactionsMenu,
      hasEquityMarketMenu,
      hasPortfolioMenu,
      hasAdministrativeMenu,
    },
    actions: {
      getBoardLinkByRole,
    },
  };
};
